import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { ReactNode } from 'react'

import { SPK_STAKING_POINTS_MULTIPLIER } from '@/config/consts'
import { Token, USD_MOCK_TOKEN } from '@/domain/types/Token'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLink, InternalLink } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { GrowingReward } from '@/ui/molecules/growing-reward/GrowingReward'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface StakeActivePanelProps {
  stakedAmount: NormalizedNumber
  stakingToken: Token
  pointsSeason: number
  previousSeasonPoints: NormalizedNumber
  calculateReward: (timestampInMs: number) => NormalizedNumber
  refreshGrowingRewardIntervalInMs: number | undefined
  arePointsOutOfSync: boolean
  openUnstakeDialog: () => void
  openStakeDialog: () => void
  className?: string
}

export function StakeActivePanel({
  stakedAmount,
  stakingToken,
  pointsSeason,
  previousSeasonPoints,
  calculateReward,
  refreshGrowingRewardIntervalInMs,
  arePointsOutOfSync,
  openUnstakeDialog,
  openStakeDialog,
  className,
}: StakeActivePanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-8 lg:gap-16',
        'bg-cover bg-right bg-no-repeat',
        'bg-[url(/src/ui/assets/spark-staking/staking-rewards-panel-bg.svg)] bg-primary-inverse',
        className,
      )}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <div className="typography-heading-4 flex items-baseline gap-1 text-primary-inverse">
          My Points
          <Info className="icon-secondary">
            You're earning Spark points from staking SPK. To see all your points, visit{' '}
            <InternalLink to="/points/{-$referralCode}">Points Page</InternalLink>. For detailed information, visit our{' '}
            <ExternalLink to={links.docs.spkStaking}>documentation</ExternalLink>
          </Info>
        </div>
        <div className={cn('grid gap-1', stakedAmount.gt(0) ? 'grid-cols-[80px_80px]' : 'grid-cols-[80px]')}>
          <Button
            onClick={openStakeDialog}
            variant="primary"
            size="m"
            data-testid={testIds.spkStaking.rewardsPanel.stakeButton}
          >
            Stake
          </Button>
          {stakedAmount.gt(0) && (
            <Button
              onClick={openUnstakeDialog}
              variant="secondary"
              size="m"
              data-testid={testIds.spkStaking.rewardsPanel.unstakeButton}
            >
              Unstake
            </Button>
          )}
        </div>
      </div>
      <GrowingReward
        rewardIconPath={assets.points.pointsIcon}
        calculateReward={calculateReward}
        refreshIntervalInMs={refreshGrowingRewardIntervalInMs}
        isOutOfSync={arePointsOutOfSync}
        wholePartTextBgGradientClass={cn('bg-[linear-gradient(180deg,#FA43BD_-7.52%,#FFCD4D_144.2%)]')}
        fractionalPartTextColorClass={cn(
          'bg-[linear-gradient(180deg,#FA43BD_-7.52%,#FFCD4D_144.2%)] bg-clip-text text-transparent',
        )}
        shadowOnFractionalPart={false}
        rewardsTextDataTestId={testIds.spkStaking.rewardsPanel.rewards}
      />
      <div className="grid grid-cols-1 gap-2 divide-fg-secondary sm:grid-cols-3 sm:gap-0 sm:divide-x">
        <DetailsItem label="My Stake" tooltip="The amount of SPK you're currently staking.">
          <div
            className="typography-label-1 flex items-center gap-1.5 text-primary-inverse"
            data-testid={testIds.spkStaking.rewardsPanel.staked}
          >
            <img src={stakingToken.logo} className="h-4" alt={stakingToken.symbol} />
            {stakingToken.format(stakedAmount, { style: 'auto' })}
          </div>
        </DetailsItem>
        <DetailsItem
          label="Multiplier"
          tooltip="Spark Points earned per staked SPK per day."
          className={cn(previousSeasonPoints.gt(0) && 'hidden sm:flex')}
        >
          <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">
            <img src={assets.points.pointsIcon} className="h-4" alt={stakingToken.symbol} />
            {SPK_STAKING_POINTS_MULTIPLIER}x
          </div>
        </DetailsItem>
        {arePointsOutOfSync && (
          <DetailsItem label="Points amount" className="hidden sm:flex">
            <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">
              <div className="flex items-center gap-1.5">
                <img
                  src={assets.spkStaking.syncingStatusIcon}
                  className="h-4"
                  alt="syncing SPK staking points icon"
                  data-chromatic="ignore"
                />
                Updating
              </div>
            </div>
          </DetailsItem>
        )}
        {!arePointsOutOfSync && previousSeasonPoints.gt(0) && (
          <DetailsItem
            label={pointsSeason === 3 ? 'Season 2 points' : 'Season 1 points'}
            tooltip={
              <>
                Visit <InternalLink to="/points/{-$referralCode}">Points Page</InternalLink> for more details.
              </>
            }
          >
            <div
              className="typography-label-1 flex items-center gap-1.5 text-primary-inverse"
              data-testid={testIds.spkStaking.rewardsPanel.staked}
            >
              <img src={assets.points.pointsIcon} className="h-4" alt={'Points'} />
              {USD_MOCK_TOKEN.format(previousSeasonPoints, { style: 'compact' })}
            </div>
          </DetailsItem>
        )}
      </div>
    </Panel>
  )
}

function DetailsItem({
  label,
  tooltip,
  children,
  className,
}: {
  label: string
  tooltip?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-row justify-between gap-1 sm:flex-col sm:px-3 sm:last:pr-0 sm:first:pl-0 xl:px-6',
        className,
      )}
    >
      <div className="typography-label-3 flex items-center gap-1 text-tertiary">
        {label}
        {tooltip && <Info className="icon-secondary">{tooltip}</Info>}
      </div>
      <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">{children}</div>
    </div>
  )
}
